import { CloseSmallIcon } from 'assets/svg'
import AnimatedModal from 'components/AnimatedModal'
import { THeaderType } from 'components/Header/Header'
import useLocale from 'hooks/useLocale'
import React, { FC, useCallback, useState } from 'react'
import styled from 'styled-components'
import NavUl from 'ui/NavUl'
import getDropdownOptions from 'utils/getDropdownOptions'
import getNavItemUrl from 'utils/getNavItemUrl'
import AuthLinks from '../AuthLinks'
import MobileLocaleAccordion from '../LocalesDropdown/MobileLocaleAccordion'
import NavBarAccordion from '../NavBarAccordion'
import NavItem from '../NavItem'

import { isEmptyHeaderPage } from '@constants'
import useTranslation from 'hooks/useTranslation'
import getHeaderMobileNavBarData from 'utils/getHeaderMobileNavBarData'

const MobileNavBar: FC<Props> = ({ pageLocales, withAuth, type }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { pathname } = useLocale()
  const { t } = useTranslation()

  const onClose = useCallback(() => {
    if (isEmptyHeaderPage(pathname)) window.parent.postMessage('false', '*')
    setIsOpen(!isOpen)
  }, [isOpen, pathname])

  const { locale } = useLocale()
  const navbar = getHeaderMobileNavBarData(t, locale)

  return (
    <>
      <BurgerButton
        onClick={() => {
          setIsOpen(true)
        }}>
        <BurgerSpan $type={type} />
      </BurgerButton>
      <AnimatedModal
        isOpen={isOpen}
        onClose={onClose}
        renderContent={isAnimated => (
          <Body $type={type} isAnimated={isAnimated}>
            <Header>
              <CloseButton $type={type} onClick={onClose}>
                <CloseSmallIcon />
              </CloseButton>
            </Header>
            <ButtonsContainer>
              <AuthLinks type={type} withAuth={withAuth} />
            </ButtonsContainer>
            <Ul>
              <SectionContainer>
                <Title $type={type}>{t('Website settings')}</Title>
                {/* <DropdownMobileApp icon={NewMobileIcon} dropdownType={'mobile'} type={type} /> */}
                <MobileLocaleAccordion type={type} pageLocales={pageLocales} />
              </SectionContainer>
              <Title $type={type}>{t('Margex Products')}</Title>
              {navbar?.map(({ id, iconLink, attributes }) => {
                const { dropdown, name, navbarLinks, warning, withLocale, includeLangsArr } = attributes || {}
                // const withLocale = [true, true, true, false]
                const to = getNavItemUrl(navbarLinks)

                return (
                  <Li $type={type} key={id}>
                    {dropdown ? (
                      <NavBarAccordion
                        iconLink={iconLink}
                        type={type}
                        name={name}
                        warning={warning}
                        onClose={onClose}
                        options={getDropdownOptions(navbarLinks?.data, locale)}
                        locale={withLocale}
                      />
                    ) : (
                      <NavItem
                        onClose={onClose}
                        iconLink={iconLink}
                        type={type}
                        name={name}
                        warning={warning}
                        to={to}
                        withLocale={withLocale}
                        includeLangsArr={includeLangsArr}
                      />
                    )}
                  </Li>
                )
              })}
            </Ul>
          </Body>
        )}
      />
    </>
  )
}

const Body = styled.div<{ isAnimated: boolean; $type: THeaderType }>`
  width: 100%;
  max-width: 448px;
  margin-inline-start: auto;
  height: 100vh;
  background: ${({ $type, theme: { colors } }) =>
    $type === 'secondary' ? colors.newDarkColor.darkBlue : colors.newLightColor.paleWhite};
  transform: ${({ isAnimated }) => (isAnimated ? 'translateX(0)' : 'translatex(100vw)')};
  transition: all 0.3s;
  overflow: overlay;
  box-shadow: 0px 4px 32px rgba(0, 0, 0, 0.3);

  ::-webkit-scrollbar {
    width: 4px;
    height: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: #2f6fea;
    border-radius: 4px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  padding: 26px 40px 0;
`

const CloseButton = styled.button<{ $type: THeaderType }>`
  display: block;
  background-color: transparent;
  border: none;
  width: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  > svg {
    & > path {
      fill: ${({ $type, theme: { colors } }) =>
        $type === 'secondary' ? colors.muted : colors.newLightColor.vividBlue};
    }
  }
`

const Ul = styled(NavUl)`
  flex-direction: column;
  margin-bottom: 100px;
  margin-bottom: 100px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
`

const Li = styled.li<{ $type: THeaderType }>`
  display: block;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid rgba(48, 113, 238, 0.1);

  @media ${({ theme }) => theme.device.xxmMax} {
    :nth-last-child(n + 2) {
      border-bottom-color: ${({ $type }) => ($type === 'primary' ? '#dfeafc' : '#1f4da9')};
      border-bottom-width: 1px;
      border-bottom-style: solid;
    }
  }

  @media ${({ theme }) => theme.device.xlMax} {
    :nth-last-child(n + 2) {
      border-bottom: none;
    }
    :nth-last-child(n + 3) {
      border-bottom-color: ${({ $type }) => ($type === 'primary' ? '#dfeafc' : '#1f4da9')};
      border-bottom-width: 1px;
      border-bottom-style: solid;
    }
  }
  :hover {
    & > a {
      color: ${({ $type, theme: { colors } }) => ($type === 'primary' ? colors.newLightColor.vividBlue : colors.white)};
      background-color: ${({ $type, theme: { colors } }) =>
        $type === 'primary' ? colors.white : `rgba(48, 113, 238, 0.2)`};
  }

  -webkit-tap-highlight-color: transparent;
`

const Title = styled.h5<{ $type: THeaderType }>`
  font-size: 1rem;
  font-weight: 600;
  padding: 17px 40px;
  color: ${({ $type, theme: { colors } }) =>
    $type === 'secondary' ? colors.newDarkColor.brightGreen : colors.newLightColor.vividBlue};
`

const ButtonsContainer = styled.div`
  width: 100%;
  padding: 0 40px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 56px;

  & > a {
    height: 48px;
  }
`

const BurgerButton = styled.button`
  margin-inline-start: 12px;
  line-height: 1;
  background-color: transparent;
  border: 0 solid transparent;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.black};
  border-color: ${({ theme }) => theme.colors.opacity};
  cursor: pointer;

  @media ${({ theme }) => theme.device.smMax} {
    margin-inline-start: 0;
  }
`

const BurgerSpan = styled.span<{ $type: THeaderType }>`
  display: block;
  position: relative;
  z-index: 10;
  width: 21px;
  color: ${({ $type, theme: { colors } }) =>
    $type === 'secondary' ? colors.newDarkColor.lightGrayishNavy : colors.newLightColor.vividBlue};
  height: 2px;
  background-color: currentColor;
  transition: color 0.2s, background-color 0.2s;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 2px;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transition: transform 0.3s;
    background-color: ${({ $type, theme: { colors } }) =>
      $type === 'secondary' ? colors.newDarkColor.white : colors.newLightColor.vividBlue};
    transform: translateY(-6px) rotate(0);
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 2px;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transition: transform 0.3s;
    background-color: ${({ $type, theme: { colors } }) =>
      $type === 'secondary' ? colors.newDarkColor.white : colors.newLightColor.vividBlue};
    transform: translateY(6px) rotate(0);
  }
`

interface Props {
  withAuth: boolean | null
  type: THeaderType
  pageLocales?: string[]
}

export default MobileNavBar
